import type { Query, QueryClient } from '@tanstack/react-query'

/**
 * The ONE post-write revalidation policy, applied to the WHOLE cache after every successful mutation
 * (wired into the MutationCache in `@/main`). Exported so the handful of call sites that must HOLD
 * their UI until the visible data is fresh can await the same policy instead of writing a second,
 * subtly-different one.
 *
 * Why whole-cache: a write can touch anything (evaluating a member changes that team's report, the
 * teams list, the employee's own report, the org rollup...), so hand-maintained per-mutation
 * invalidation lists rot silently — the one you forgot shows stale data and nothing fails.
 *
 * Why two halves — "revalidate" means something different for what the user is LOOKING AT vs. what
 * they might navigate to next:
 *
 *  - ACTIVE (has observers) → invalidate: refetch in place while KEEPING the data, so the current
 *    screen never falls back to its skeleton on a write. `useSuspenseQuery` suspends only when a key
 *    has no data, and invalidation removes none; the page keeps rendering the previous value with
 *    `isFetching` true (what drives FetchingProgress).
 *  - INACTIVE (cached but unobserved: hover-preloads from `defaultPreload: 'intent'`, the route just
 *    navigated away from, every member/filter combination visited this session) → reset: drop the
 *    data so the next visit suspends into that route's own pendingComponent. Merely marking them
 *    stale would paint the STALE value first and swap it under the user a moment later. Clearing
 *    costs nothing until then — a query with no observers doesn't refetch.
 *
 * ...with one carve-out inside that second half: an inactive query that is still IN FLIGHT is a route
 * loader's fire-and-forget `query()`, which has no observers until its component subscribes.
 * `resetQueries` would ABORT it (reset → destroy → cancel), so mutating and immediately navigating
 * would kill the destination's prefetch and drop the route into its pendingComponent. Those are left to
 * land and invalidated once they settle instead: by then the route has usually mounted, so the
 * invalidation refetches in place (keeping data) rather than blanking it, and if nothing mounted it
 * simply marks the entry stale for whoever reads it next.
 *
 * Returns the ACTIVE refetch promise (the inactive reset is local bookkeeping with nothing to wait
 * on), so `await revalidateAfterWrite(client)` resolves exactly when what's on screen is fresh.
 *
 * `predicate` narrows BOTH halves, for the rare write that must hold a specific key back — today only
 * the employer switch, which optimistically writes `getUser` itself and would race its own refetch.
 * It is not a way to hand-pick what a mutation touches; that's the coupling this exists to delete.
 */
export function revalidateAfterWrite(client: QueryClient, predicate?: (query: Query) => boolean): Promise<void> {
  const refetched = client.invalidateQueries({ type: 'active', predicate })
  void client.resetQueries({ type: 'inactive', fetchStatus: 'idle', predicate })

  // Not `fetchStatus: 'fetching'` — that would miss a request PAUSED offline, which is equally
  // mid-flight and equally not something to cancel.
  const inFlight = client
    .getQueryCache()
    .findAll({ type: 'inactive', predicate })
    .filter((query) => query.state.fetchStatus !== 'idle')

  if (inFlight.length > 0) {
    // `?? Promise.resolve()` because `query.promise` is undefined for a query that has never fetched;
    // it cannot be one here (they are all mid-flight), but the type allows it.
    void Promise.allSettled(inFlight.map((query) => query.promise ?? Promise.resolve())).then(() => {
      for (const query of inFlight) void client.invalidateQueries({ queryKey: query.queryKey, exact: true })
    })
  }

  return refetched
}
